import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useMatch } from "react-router-dom";

import {
  ChevronDown,
  LogoMonogram,
  LogoWordmark,
  QuestionMark,
} from "@/assets/icons/icons";
import { LanguageFooter } from "@/components/Layout/BaseLayouts/Sidebar/Navigation/LanguageFooter";
import { NavIcon } from "@/components/Layout/BaseLayouts/Sidebar/Navigation/NavIcon";
import { NavPanelContent } from "@/components/Layout/BaseLayouts/Sidebar/Navigation/NavPanelContent";
import {
  PortalNavigationItem,
  usePortalShellConfig,
} from "@/components/Layout/PortalShellConfig";
import { CockpitTodosSection } from "@/components/Portal/Cockpit/CockpitTodosSection/CockpitTodosSection";

const CollapsedNavItem = ({
  item,
  showFriendsWithBenefitsAttentionBubble,
}: {
  item: PortalNavigationItem;
  showFriendsWithBenefitsAttentionBubble: boolean;
}) => {
  const { t } = useTranslation();
  const match = useMatch(item.target + "/*");
  const ariaLabelKey =
    ("ariaLabel" in item ? item.ariaLabel : undefined) ?? item.text;

  return (
    <li>
      <Link
        className={clsx(
          "flex items-center justify-center rounded-md p-2 text-foreground-primary hover:bg-surface-tertiary",
          match && "bg-surface-tertiary",
        )}
        to={item.target}
        aria-label={String(t(ariaLabelKey as never))}
        aria-current={match ? "page" : undefined}
      >
        <NavIcon
          item={item}
          showAttentionBubble={showFriendsWithBenefitsAttentionBubble}
        />
      </Link>
    </li>
  );
};

/**
 * Desktop sidebar: collapsible between an icon-only rail and a full panel
 * with labels, support/legal links, logout and language switcher. Renders
 * in place of the old `TODO: replace SideMenu` placeholder.
 */
export const Sidebar = () => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const {
    homeLink,
    navigationItems,
    showCockpitTodos,
    showFriendsWithBenefitsAttentionBubble,
  } = usePortalShellConfig();

  // The app shell (App.tsx) used to set `overflow-hidden` on an ancestor, which
  // made *that* element the sticky positioning context -- and since it never
  // scrolled itself, `position: sticky` never re-anchored here. It now uses
  // `overflow-x-clip` so sticky works for edit headers; the sidebar stays
  // `fixed` so it remains viewport-pinned regardless.
  // `fixed` isn't affected by ancestor overflow, so the panel is fixed to the
  // viewport and this wrapper just reserves its width in the flex row.
  if (collapsed) {
    return (
      <div className="w-16 shrink-0" data-testid="sidebar">
        <div className="fixed left-0 top-0 z-20 flex h-screen w-16 flex-col items-center gap-4 overflow-y-auto border-r border-border-tertiary bg-background-primary p-2">
          <button
            className="flex items-center justify-center rounded-md p-2 hover:bg-surface-tertiary"
            type="button"
            aria-label={t("layout.menu.sidebar.expand")}
            onClick={() => setCollapsed(false)}
          >
            <ChevronDown className="size-4 -rotate-90 stroke-foreground-primary" />
          </button>
          <ul className="flex flex-col gap-1">
            {navigationItems.map((item) => (
              <CollapsedNavItem
                key={item.target}
                item={item}
                showFriendsWithBenefitsAttentionBubble={
                  showFriendsWithBenefitsAttentionBubble
                }
              />
            ))}
          </ul>

          {/* Collapsed rail: divider, then Support + DR below the nav
              items (language + logout intentionally hidden while collapsed) */}
          <hr className="w-6 border-t border-border-tertiary" />
          <ul className="flex flex-col gap-1">
            <li>
              <a
                className="flex items-center justify-center rounded-md p-2 text-foreground-primary hover:bg-surface-tertiary"
                href={t("Footer.support.url")}
                aria-label={t("Common.support")}
              >
                <QuestionMark className="size-6" />
              </a>
            </li>
            <li>
              <button
                className="flex items-center justify-center rounded-md p-2 text-foreground-primary hover:bg-surface-tertiary"
                type="button"
                aria-label={`${t("Common.logoName")}: ${t("layout.menu.sidebar.expand")}`}
                onClick={() => setCollapsed(false)}
              >
                <LogoMonogram className="size-6" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sidebar" className="w-[280px] shrink-0">
      <div className="fixed left-0 top-0 z-20 flex h-screen w-[280px] flex-col gap-4 overflow-y-auto border-r border-border-tertiary bg-background-primary p-3">
        <div className="flex items-center gap-2">
          {homeLink ? (
            <Link to={homeLink} aria-label={t("Common.logoName")}>
              <LogoWordmark className="h-8 w-16 text-foreground-primary" />
            </Link>
          ) : (
            <LogoWordmark
              className="h-8 w-16 text-foreground-primary"
              aria-label={t("Common.logoName")}
            />
          )}
          <button
            className="ml-auto flex items-center justify-center rounded-md p-2 hover:bg-surface-tertiary"
            type="button"
            aria-label={t("layout.menu.sidebar.collapse")}
            onClick={() => setCollapsed(true)}
          >
            <ChevronDown className="size-4 rotate-90 stroke-foreground-primary" />
          </button>
        </div>

        <NavPanelContent />

        {/* Open to-dos live here from `desktop` up — below that the cockpit
            renders the same list in the content column, never in the burger
            menu. `mt-auto` pushes both it and the language switcher to the
            bottom of the panel. */}
        <div className="mt-auto flex flex-col gap-4">
          {showCockpitTodos && <CockpitTodosSection />}
          <LanguageFooter />
        </div>
      </div>
    </div>
  );
};
